import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { CreditCard, DollarSign, Bookmark } from 'lucide-react';
import StripeCheckout from './StripeCheckout';
import PayPalButton from './PayPalButton';
import SavedPaymentMethods from './SavedPaymentMethods';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

const PaymentMethodSelector = ({ 
  amount, 
  description, 
  rideId, 
  packageType,
  onSuccess, 
  onError 
}) => {
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
  const [payingWithSaved, setPayingWithSaved] = useState(false);

  const paymentMethods = [
    {
      id: 'saved',
      name: 'Saved Cards',
      icon: Bookmark,
      description: 'Use a saved payment method'
    },
    {
      id: 'stripe',
      name: 'Credit/Debit Card',
      icon: CreditCard,
      description: 'Pay securely with Stripe'
    },
    {
      id: 'paypal',
      name: 'PayPal',
      icon: DollarSign,
      description: 'Pay with your PayPal account'
    }
  ];

  const handlePayWithSaved = async () => {
    if (!selectedPaymentMethodId) {
      toast.error('Please select a payment method');
      return;
    }

    setPayingWithSaved(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/payment-methods/${selectedPaymentMethodId}/charge`,
        {
          amount: parseFloat(amount),
          description: description,
          ride_id: rideId
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      toast.success('Payment completed successfully!');
      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Payment failed. Please try again.');
      if (onError) onError(error);
    } finally {
      setPayingWithSaved(false);
    }
  };

  return (
    <div className="space-y-4">
      {!selectedMethod ? (
        <>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Choose Payment Method
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {paymentMethods.map((method) => (
              <Card
                key={method.id}
                className="cursor-pointer hover:shadow-lg transition border-2 hover:border-blue-500"
                onClick={() => setSelectedMethod(method.id)}
              >
                <CardContent className="pt-6">
                  <div className="flex flex-col items-center text-center">
                    <div className="bg-blue-100 p-4 rounded-full mb-3">
                      <method.icon className="w-8 h-8 text-blue-600" />
                    </div>
                    <h4 className="font-bold text-gray-900 mb-1">{method.name}</h4>
                    <p className="text-sm text-gray-600">{method.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              {selectedMethod === 'saved' && 'Select Saved Card'}
              {selectedMethod === 'stripe' && 'Pay with Card'}
              {selectedMethod === 'paypal' && 'Pay with PayPal'}
            </h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSelectedMethod(null);
                setSelectedPaymentMethodId(null);
              }}
            >
              Change Method
            </Button>
          </div>

          {selectedMethod === 'saved' && (
            <div className="space-y-4">
              <SavedPaymentMethods
                onSelectPaymentMethod={setSelectedPaymentMethodId}
                selectedPaymentMethodId={selectedPaymentMethodId}
                showAddNew={true}
              />
              {selectedPaymentMethodId && (
                <Button
                  onClick={handlePayWithSaved}
                  disabled={payingWithSaved}
                  className="w-full"
                  size="lg"
                >
                  {payingWithSaved ? 'Processing...' : `Pay $${parseFloat(amount).toFixed(2)}`}
                </Button>
              )}
            </div>
          )}

          {selectedMethod === 'stripe' && (
            <StripeCheckout
              amount={amount}
              rideDetails={{
                description: description,
                ride_id: rideId,
                package_type: packageType
              }}
              onSuccess={onSuccess}
            />
          )}

          {selectedMethod === 'paypal' && (
            <PayPalButton
              amount={amount}
              description={description}
              rideId={rideId}
              packageType={packageType}
              onSuccess={onSuccess}
              onError={onError}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default PaymentMethodSelector;
